import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import PatientProgressNotesService from "../../service/PatientProgressNotesService";
import { TAGS, noteAuthor, tagValue, sanitizeBody } from "../../helper/progressNoteFormatters";
import AuthenticationService from "service/auth/AuthenticationService";
import EventService from "service/event/EventService";
import DatePicker from "components/common/DatePicker";

/**
 * Progress-note composer — the full-screen modal, in BOTH its modes. Source:
 * Figma composer board: "New progress note" (filled 2162:3257, empty 2167:3257)
 * and "New comment" (2168:3257). Portalled to document.body (like the
 * appointments Dialog) so it overlays the whole patient shell rather than sitting
 * in a column. Shell: #f9f9fb, radius 14, centered on a rgba(0,0,0,.4) backdrop.
 *
 * Layout: a 60px white top bar (close-X + title on the left, orange Save on the
 * right) over a two-pane body — LEFT (600px): a single-select category-tab row +
 * a rich-text editor card (a toolbar strip of dropdown pills and B/I/U/S · list ·
 * link/image icon buttons, then the body textarea). RIGHT (496px): a "Tasks (N)"
 * header + "+ Add task" and Task cards (Description + Reset, Project, Assign to,
 * Due Date, remove-task).
 *
 * `replyingTo` switches it to COMMENT mode. Figma draws the two screens as one
 * shell and they differ in exactly two places, so this is one component rather
 * than two: the title, and the LEFT pane's tab row becoming a "REPLYING TO" quote
 * of the parent note. Forking would leave the editor and the whole Tasks panel
 * duplicated and free to drift apart.
 *
 * Wired to the real API, porting bridge's AddProgressNote / AddComment:
 *   - a NOTE's category tab IS its `tag` int (tagValue) and is REQUIRED; a
 *     COMMENT has no tag at all (the wire has no slot for one)
 *   - the tasks panel drives Zoho (/v2/tm/*), one POST per task, in both modes
 *   - Save creates the Zoho tasks FIRST, and writes only after they all land
 *
 * The rich-text TOOLBAR stays inert (this is a textarea, not a Draft.js editor —
 * bridge's toolbar is the one piece we did not port); its buttons keep their
 * Figma look and a "not wired up yet" title.
 */

/* ---- inline icons (no props → nothing to type-check) ---- */
const CloseIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" width="20" height="20">
    <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const Chevron = () => (
  <svg className="pn-chevron" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M3 4.5L6 7.5l3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const BulletIcon = () => (
  <svg viewBox="0 0 17 17" fill="none" aria-hidden="true" width="17" height="17">
    <circle cx="3" cy="4.5" r="1.2" fill="currentColor" />
    <circle cx="3" cy="12.5" r="1.2" fill="currentColor" />
    <path d="M6.5 4.5h8M6.5 12.5h8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);
const NumberListIcon = () => (
  <svg viewBox="0 0 17 17" fill="none" aria-hidden="true" width="17" height="17">
    <path d="M7 4.5h8M7 12.5h8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M2 3.2h1v3M2 6.2h1.6M2 11h1.4v.9L2 13.5h1.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const LinkIcon = () => (
  <svg viewBox="0 0 17 17" fill="none" aria-hidden="true" width="17" height="17">
    <path d="M7 10a2.5 2.5 0 003.5 0l2-2A2.5 2.5 0 009 4.5l-.8.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M10 7a2.5 2.5 0 00-3.5 0l-2 2A2.5 2.5 0 008 12.5l.8-.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);
const MinusIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" aria-hidden="true" width="14" height="14">
    <path d="M3.5 7h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/** Composer category tabs never include the "ALL" pseudo-filter. */
const TAB_CATEGORIES = TAGS.map((t) => t.name).filter((n) => n !== "ALL");

/** Bridge caps a note at 10 attached tasks. */
const MAX_TASKS = 10;

/**
 * The header that opens the spliced task block, per mode.
 *
 * These two differ by ONE leading `<br>`, and that is bridge's difference, not a
 * typo we are copying: AddProgressNote opens with `" <br> <b> …"`, AddComment
 * without it. Kept faithful per-path on purpose — the string lands verbatim in a
 * STORED body that bridge renders, so "tidying" the inconsistency away here would
 * silently change the spacing of every comment phr writes relative to every one
 * bridge has written.
 */
const TASK_HEADER = {
  note: " <br> <b> Task assigned to </b> <br><ol> ",
  comment: " <b> Task assigned to </b> <br><ol> ",
};

/* No SCSS may be added from here (this file is the only one in scope), so the
 * two states the stylesheet has no rule for — a disabled Save and an inline
 * validation message — carry their look inline. */
const ERROR_STYLE = { display: "block", marginTop: 6, color: "#e5484d", fontSize: 11.5 };
const DISABLED_STYLE = { opacity: 0.5, cursor: "not-allowed" };

/**
 * The due date is a local "YYYY-MM-DD" (DatePicker's toDateKey, as the native
 * date input's value was), and Zoho wants "MM-DD-YYYY" (bridge got there via
 * moment; there is no moment here). Slicing the string rather than
 * round-tripping through Date also sidesteps the UTC-parse shift that would
 * silently post the previous day for anyone east of Greenwich.
 */
const toZohoDate = (iso) => {
  const [y, m, d] = String(iso || "").split("-");
  return y && m && d ? `${m}-${d}-${y}` : "";
};

/** A task row's blank slate. `key` only exists to give React a stable identity. */
let taskSeq = 0;
const emptyTask = () => ({
  key: (taskSeq += 1),
  taskText: "",
  taskDate: "",
  selectedProject: null,
  selectedUser: null,
  users: [],
});

/* The toolbar's own glyphs. Drawn rather than pulled from a pack: the composer
   has no icon dependency and one more 20-line SVG is cheaper than one. */
const AlignIcon = ({ lines }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
    {lines.map((w, i) => (
      <rect key={i} x={w[0]} y={3 + i * 3} width={w[1]} height="1.6" rx="0.8" fill="currentColor" />
    ))}
  </svg>
);
const IndentIcon = ({ out }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
    <rect x="6" y="2" width="8" height="1.6" rx="0.8" fill="currentColor" />
    <rect x="6" y="7.2" width="8" height="1.6" rx="0.8" fill="currentColor" />
    <rect x="6" y="12.4" width="8" height="1.6" rx="0.8" fill="currentColor" />
    <path
      d={out ? "M4 5 L1 8 L4 11" : "M1 5 L4 8 L1 11"}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);
const UnlinkIcon = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
    <path d="M6.5 9.5 3.8 12.2a2.6 2.6 0 0 1-3.7-3.7L2.8 5.8" />
    <path d="M9.5 6.5l2.7-2.7a2.6 2.6 0 0 1 3.7 3.7l-2.7 2.7" />
    <path d="M2 2l12 12" />
  </svg>
);
/* The embed glyph — a framed play triangle, legacy's slot for it. */
const EmbedIcon = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
    <rect x="1.2" y="2.6" width="13.6" height="10.8" rx="1.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <path d="M6.6 5.9v4.2L10.4 8z" fill="currentColor" />
  </svg>
);
const ImageIcon = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
    <rect x="1.2" y="2.6" width="13.6" height="10.8" rx="1.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="5.4" cy="6.3" r="1.2" fill="currentColor" />
    <path d="M2.4 12 6 8.6l2.3 2.2 2.4-2.6 2.9 3.8z" fill="currentColor" />
  </svg>
);
const ClearIcon = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor"
    strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7.5 2.5h6M5.2 13.5h8.3" />
    <path d="M10.6 2.5 7.2 13.5" />
    <path d="M1.6 6.2l4.2 4.2M5.8 6.2l-4.2 4.2" />
  </svg>
);
const UndoIcon = ({ redo }) => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={redo ? { transform: "scaleX(-1)" } : undefined}>
    <path d="M5.5 4.5 2.5 7.5 5.5 10.5" />
    <path d="M2.5 7.5h6.8a3.7 3.7 0 0 1 0 7.4H6" />
  </svg>
);

/* ── THE OPTION LISTS ARE LEGACY'S, VERBATIM ──────────────────────────────
   Read out of react-draft-wysiwyg's own bundle (its `defaultToolbar`), which
   is what legacy's AddProgressNote renders with no `toolbar` prop — so these
   are the exact options, in the exact order, a clinician has on that screen
   (Nittin, 2026-09-24: "can't we use same editing options exactly").

   Its toolbar order: inline, blockType, fontSize, fontFamily, list, textAlign,
   colorPicker, link, embedded, emoji, image, remove, history — which is the
   order the row is built in below. */

/* blockType. formatBlock takes each of these; draft's "Code" block is a <pre>. */
const BLOCKS = [
  { tag: "p", label: "Normal" },
  { tag: "h1", label: "H1" },
  { tag: "h2", label: "H2" },
  { tag: "h3", label: "H3" },
  { tag: "h4", label: "H4" },
  { tag: "h5", label: "H5" },
  { tag: "h6", label: "H6" },
  { tag: "blockquote", label: "Blockquote" },
  { tag: "pre", label: "Code" },
];

/* fontSize, in PIXELS. execCommand's own fontSize only takes 1-7 (seven
   keyword sizes), so applying one of these goes through applySize below. */
const SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96];

/* WITH NOTHING SELECTED, a size, font or colour is for what is typed NEXT
   (Nittin, 2026-10-08: "if we selected font size 72 then it should type like
   that"). The browser keeps a "typing style" for exactly this — the same one
   Bold already uses — and these commands set it. Size has no px form there:
   execCommand's fontSize has seven keyword steps, so a caret size sets the
   top step, 7, and normalizeTyped puts the typed text back to the px that was
   picked. */
const CARET_COMMANDS = { fontFamily: "fontName", color: "foreColor", backgroundColor: "hiliteColor" };
const CARET_SIZE_STEP = "7";
/* <font size> 1-6 can only arrive by paste; 7 is the caret size above. */
const FONT_STEP_PX = { 1: "10px", 2: "13px", 3: "16px", 4: "18px", 5: "24px", 6: "32px" };

/* Commands that rebuild the block the caret is in. Chrome's lists put the
   caret back at the START of the line ("hello", Bulleted list, "abc" read
   "abchello" — measured); the rest keep it, so for them the check below is a
   no-op. */
const BLOCK_COMMANDS = new Set([
  "insertUnorderedList", "insertOrderedList", "indent", "outdent", "formatBlock",
  "justifyLeft", "justifyCenter", "justifyRight", "justifyFull",
]);

/* The selection as character offsets into the editor's text. A block command
   restructures the markup but never the text, so these survive it. */
const textOffsets = (root) => {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;
  const r = sel.getRangeAt(0);
  if (!root.contains(r.startContainer) || !root.contains(r.endContainer)) return null;
  const upTo = document.createRange();
  upTo.selectNodeContents(root);
  upTo.setEnd(r.startContainer, r.startOffset);
  const start = upTo.toString().length;
  upTo.setEnd(r.endContainer, r.endOffset);
  return { start, end: upTo.toString().length };
};

/* The text node and offset `offset` characters in, or null with no text. */
const textPoint = (root, offset) => {
  const walk = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let left = offset;
  let last = null;
  for (let n = walk.nextNode(); n; n = walk.nextNode()) {
    if (left <= n.nodeValue.length) return { node: n, offset: left };
    left -= n.nodeValue.length;
    last = n;
  }
  return last ? { node: last, offset: last.nodeValue.length } : null;
};

/* A <font> -> the <span style> the sanitizer keeps (it strips font's
   attributes, so a <font> would save as plain text). Children are MOVED, not
   copied, so a caret inside them stays valid. */
const fontToSpan = (font, sizePx) => {
  const span = document.createElement("span");
  const own = font.getAttribute("style");
  if (own) span.setAttribute("style", own);
  const face = font.getAttribute("face");
  const color = font.getAttribute("color");
  const size = font.getAttribute("size");
  if (face) span.style.fontFamily = face;
  if (color) span.style.color = color;
  if (size) span.style.fontSize = size === CARET_SIZE_STEP ? sizePx : FONT_STEP_PX[size] || "";
  while (font.firstChild) span.appendChild(font.firstChild);
  font.parentNode.replaceChild(span, font);
  return span;
};

/* fontFamily. Legacy's six — Montserrat is deliberately NOT added: the point
   is the same list, and the editor already renders in the app's face. */
const FONTS = ["Arial", "Georgia", "Impact", "Tahoma", "Times New Roman", "Verdana"];

/* colorPicker: the same 27 swatches, in the same order, for both text and
   background — legacy's picker has the two tabs and so does this one. */
const COLORS = [
  "rgb(97,189,109)", "rgb(26,188,156)", "rgb(84,172,210)", "rgb(44,130,201)",
  "rgb(147,101,184)", "rgb(71,85,119)", "rgb(204,204,204)", "rgb(65,168,95)",
  "rgb(0,168,133)", "rgb(61,142,185)", "rgb(41,105,176)", "rgb(85,57,130)",
  "rgb(40,50,78)", "rgb(0,0,0)", "rgb(247,218,100)", "rgb(251,160,38)",
  "rgb(235,107,86)", "rgb(226,80,65)", "rgb(163,143,132)", "rgb(239,239,239)",
  "rgb(255,255,255)", "rgb(250,197,28)", "rgb(243,121,52)", "rgb(209,72,65)",
  "rgb(184,49,47)", "rgb(124,112,107)", "rgb(209,213,216)",
];

/* emoji: legacy's 129, verbatim and in order. */
const EMOJIS = [
  "😀", "😁", "😂", "😃", "😉", "😋", "😎", "😍", "😗", "🤗", "🤔", "😣", "😫", "😴", "😌",
  "🤓", "😛", "😜", "😠", "😇", "😷", "😈", "👻", "😺", "😸", "😹", "😻", "😼", "😽", "🙀",
  "🙈", "🙉", "🙊", "👼", "👮", "🕵", "💂", "👳", "🎅", "👸", "👰", "👲", "🙍", "🙇", "🚶",
  "🏃", "💃", "⛷", "🏂", "🏌", "🏄", "🚣", "🏊", "⛹", "🏋", "🚴", "👫", "💪", "👈", "👉",
  "👆", "🖕", "👇", "🖖", "🤘", "🖐", "👌", "👍", "👎", "✊", "👊", "👏", "🙌", "🙏", "🐵",
  "🐶", "🐇", "🐥", "🐸", "🐌", "🐛", "🐜", "🐝", "🍉", "🍄", "🍔", "🍤", "🍨", "🍪", "🎂",
  "🍰", "🍾", "🍷", "🍸", "🍺", "🌍", "🚑", "⏰", "🌙", "🌝", "🌞", "⭐", "🌟", "🌠", "🌨",
  "🌩", "⛄", "🔥", "🎄", "🎈", "🎉", "🎊", "🎁", "🎗", "🏀", "🏈", "🎲", "🔇", "🔈", "📣",
  "🔔", "🎵", "🎷", "💰", "🖊", "📅", "✅", "❎", "💯",
];

/* embedded: legacy's button takes a URL and frames it. An iframe in a clinical
   note is the one control here with a real blast radius, so only the hosts a
   video can legitimately come from are framed — everything else the sanitizer
   drops on the way out (progressNoteFormatters). */
const EMBED_HOSTS = /^(?:www\.)?(?:youtube\.com|youtu\.be|player\.vimeo\.com|vimeo\.com)$/i;

const ComposerModal = ({ patient, replyingTo, initialCategory, onClose, onCreated, onSubmitComment }) => {
  // The one switch between the two Figma screens. Everything mode-dependent below
  // reads this rather than re-testing the prop, so the two screens cannot drift
  // apart in one place and not another.
  const isComment = Boolean(replyingTo);
  const subject = isComment ? "comment" : "note";

  /* OPENS ON THE CHIP THE FEED IS FILTERED BY (Nittin, 2026-09-24). Reading
     CALL_UPDATE and pressing "+ Add new" means writing a CALL_UPDATE; picking
     the tab again is a step the screen already knows the answer to.

     Seeded in the INITIALISER, not an effect: an effect runs after the first
     render, so the modal would paint with no tab and the Save button's
     "Pick a category" gate on, then flip — and a user typing in that first
     frame would see the tab change under them.

     A COMMENT has no category (it inherits the note's), and "ALL" is the
     feed's pseudo-chip rather than a real tag, so neither seeds anything: the
     guard is the tab row's own list, so an unknown name cannot select a tab
     that does not exist. */
  const [activeTab, setActiveTab] = useState(() =>
    !isComment && TAB_CATEGORIES.indexOf(initialCategory) !== -1 ? initialCategory : null
  );
  const [body, setBody] = useState(""); // the editor's HTML
  const [bodyText, setBodyText] = useState(""); // its plain text (for the Save rule)
  const [blockLabel, setBlockLabel] = useState("Normal");
  /* Legacy's size and font pills show the CURRENT value, not the word "Size" —
     its defaults are 14 and the first family in the list. */
  const [sizeLabel, setSizeLabel] = useState("14");
  const [fontLabel, setFontLabel] = useState("Font");
  /* Which toolbar menu is open — "size" | "font" | "color" | "emoji" | null.
     ONE piece of state for all four, so opening a second closes the first
     without four effects arguing about it. */
  const [menu, setMenu] = useState(null);
  /* Which half of the colour picker is showing — legacy's two tabs. */
  const [colorMode, setColorMode] = useState("color");
  const editorRef = useRef(null);
  const blockRef = useRef(null);
  const menuRef = useRef(null);
  /* The px a size was picked at with nothing selected (CARET_COMMANDS). */
  const pendingSizeRef = useRef("");
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [taskErrors, setTaskErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const service = useRef(new PatientProgressNotesService()).current;
  const auth = useRef(new AuthenticationService()).current;

  // Every response below lands in an async callback that can outlive the modal
  // (Escape closes it mid-flight), so each one checks this before setState.
  const mounted = useRef(true);
  /* A task's due-date calendar is open: Escape is then the calendar's, not the
     composer's (RescheduleModal's popoverOpenRef pattern). */
  const popoverOpenRef = useRef(false);
  const onCalendarOpenChange = useCallback((open) => {
    popoverOpenRef.current = open;
  }, []);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const showError = useCallback((message) => {
    EventService.emit("showError", { message });
  }, []);

  /* ---- rich-text editor (contentEditable + execCommand, no deps) ---- */
  // The editor is uncontrolled DOM (React can't drive contentEditable via a
  // value), so we mirror its HTML + plain text into state on every input.
  const syncBody = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    setBody(el.innerHTML);
    setBodyText(el.textContent || "");
  }, []);

  /* A caret size, font or colour (CARET_COMMANDS) lands as a <font size|face|
     color> round what is typed, and the sanitizer strips font's attributes:
     the formatting would vanish on save, and size 7 is the browser's 48px, not
     the px that was picked. Each becomes the <span style> a selection gets.
     fontToSpan MOVES the children, so the caret's text node survives; the
     selection is put back on it, and on the new span where it pointed at the
     <font> itself. */
  const normalizeTyped = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const px = pendingSizeRef.current || "48px";
    /* The same step in its CSS form, should styleWithCSS have been on. */
    el.querySelectorAll("span[style]").forEach((s) => {
      if (/xxx-large/.test(s.style.fontSize)) s.style.fontSize = px;
    });
    const fonts = el.querySelectorAll("font");
    if (!fonts.length) return;
    const sel = window.getSelection();
    const keep = sel && sel.rangeCount
      ? [sel.anchorNode, sel.anchorOffset, sel.focusNode, sel.focusOffset]
      : null;
    const swapped = new Map();
    fonts.forEach((f) => swapped.set(f, fontToSpan(f, px)));
    if (!keep) return;
    const anchor = swapped.get(keep[0]) || keep[0];
    const focus = swapped.get(keep[2]) || keep[2];
    if (el.contains(anchor) && el.contains(focus)) {
      sel.setBaseAndExtent(anchor, keep[1], focus, keep[3]);
    }
  }, []);

  /* Never under an IME's feet: moving the nodes it is composing into cancels
     the composition. compositionend comes back through here. */
  const onEditorInput = useCallback(
    (e) => {
      if (!(e && e.nativeEvent && e.nativeEvent.isComposing)) normalizeTyped();
      syncBody();
    },
    [normalizeTyped, syncBody]
  );

  /* Apply a formatting command to the current selection.

     styleWithCSS OFF by default forces semantic tags (<b>/<i>/<u>/<s>) over
     inline-style spans, which is what sanitizeBody keeps and what reads
     sensibly in the patient app.

     Size, font and colour run with the flag off too. On a selection they do
     not come through here at all (wrapSelection wraps it in a span); with
     nothing selected they emit <font size|face|color> once typed, which
     DOMPurify 3.4 would strip the attributes of even when ALLOWED_ATTR names
     them (measured), so normalizeTyped turns each into a <span style>. */
  const exec = useCallback(
    (command, value, css) => {
      const el = editorRef.current;
      if (el) el.focus();
      const before = el && BLOCK_COMMANDS.has(command) ? textOffsets(el) : null;
      try {
        document.execCommand("styleWithCSS", false, !!css);
      } catch (e) {
        /* not all engines support the toggle; the command still runs */
      }
      document.execCommand(command, false, value);
      /* Put the caret back only if the command MOVED it: the text did not
         change, so the same offsets are the same place. (On an empty line
         there is no text to count, and nothing moves.) */
      const after = before && textOffsets(el);
      if (before && (!after || after.start !== before.start || after.end !== before.end)) {
        const from = textPoint(el, before.start);
        const to = textPoint(el, before.end);
        if (from && to) window.getSelection().setBaseAndExtent(from.node, from.offset, to.node, to.offset);
      }
      syncBody();
    },
    [syncBody]
  );

  const insertLink = useCallback(() => {
    // eslint-disable-next-line no-alert
    const url = window.prompt("Link URL (https://…)");
    if (url && url.trim()) exec("createLink", url.trim());
  }, [exec]);

  /* An IMAGE is a URL, not an upload — the same thing legacy's editor does
     (react-draft-wysiwyg's default image option has no uploadCallback, so it
     only takes a link). The sanitizer pins the scheme to http(s), so a data:
     payload cannot be pasted into a note body this way. */
  const insertImage = useCallback(() => {
    // eslint-disable-next-line no-alert
    const url = window.prompt("Image URL (https://…)");
    if (url && url.trim()) exec("insertImage", url.trim());
  }, [exec]);

  /* execCommand has no "code", so the selection is wrapped by hand. The text is
     escaped first: insertHTML PARSES what it is given, and a clinician's "<"
     would otherwise become a tag. */
  const codeSpan = useCallback(() => {
    const sel = window.getSelection();
    const text = sel ? String(sel) : "";
    const esc = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    exec("insertHTML", `<code>${esc || "&nbsp;"}</code>`);
    if (text) return;
    /* Nothing was selected, so the span holds a placeholder space to give the
       caret somewhere to be. SELECT it, so the first character typed replaces
       it: the caret used to sit after it, and every code span typed this way
       started with a stray space. */
    const now = window.getSelection();
    const at = now && now.anchorNode;
    const host = at && (at.nodeType === 1 ? at : at.parentElement);
    const code = host && host.closest("code");
    if (code && editorRef.current && editorRef.current.contains(code)) {
      const r = document.createRange();
      r.selectNodeContents(code);
      now.removeAllRanges();
      now.addRange(r);
    }
  }, [exec]);

  /* SIZE, FONT and COLOUR all go through styleWithCSS OFF, which emits
     <font size|face|color> — measured, not assumed. The menu closes itself so
     the next click lands in the editor. */
  /* SIZE, FONT AND COLOUR WRAP THE SELECTION THEMSELVES.
     execCommand can do all three, but only by REWRITING what is already there:
     applying a CSS font-size over bold text made Chrome replace the <b> with a
     span carrying `font-weight: normal` — the bold silently lost. (Its own
     fontSize also takes 1-7, seven keyword sizes, so legacy's 8..96 could not
     be applied with it anyway.)
     Wrapping is what an editor does instead: extractContents keeps every tag
     inside the selection exactly as it is, including <b>, <sup> and lists, and
     the new span only adds the one property being set.
     WITH NOTHING SELECTED there is nothing to wrap: the choice is for what is
     typed next, so it becomes the browser's typing style (CARET_COMMANDS). */
  const wrapSelection = useCallback(
    (prop, value) => {
      const el = editorRef.current;
      if (el) el.focus();
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      /* Only inside the editor: a selection that starts outside it would
         otherwise be moved into the note. */
      if (el && !el.contains(range.commonAncestorContainer)) return;
      if (sel.isCollapsed) {
        /* styleWithCSS stays OFF here, as for Bold. The flag in force when the
           first character is TYPED decides the markup, not the one in force
           now: with it on, a Bold pressed after the size came out as
           font-weight inside one span. Off, every order comes out as plain
           tags plus a <font>, which normalizeTyped turns into the same span
           this function makes for a selection. */
        if (prop === "fontSize") {
          pendingSizeRef.current = value;
          exec("fontSize", CARET_SIZE_STEP);
        } else {
          exec(CARET_COMMANDS[prop], value);
        }
        setMenu(null);
        return;
      }
      const span = document.createElement("span");
      span.style[prop] = value;
      span.appendChild(range.extractContents());
      range.insertNode(span);
      /* Keep the text selected, so a second choice applies to the same words. */
      const after = document.createRange();
      after.selectNodeContents(span);
      sel.removeAllRanges();
      sel.addRange(after);
      syncBody();
      setMenu(null);
    },
    [syncBody, exec]
  );
  const applySize = useCallback((px) => wrapSelection("fontSize", `${px}px`), [wrapSelection]);
  const applyFont = useCallback((f) => wrapSelection("fontFamily", f), [wrapSelection]);
  /* Legacy's picker has these two tabs; both are one wrapped span here. */
  const applyColor = useCallback(
    (value) => wrapSelection(colorMode === "bg" ? "backgroundColor" : "color", value),
    [wrapSelection, colorMode]
  );

  /* EMBEDDED. Legacy's button frames whatever URL it is given, because that is
     react-draft-wysiwyg's default. An iframe in a clinical note is the one
     control here with a real blast radius, so this frames only the hosts a
     video legitimately comes from and turns a watch link into its embed form;
     anything else is refused here AND dropped by the sanitizer on the way out,
     so a pasted body cannot smuggle one in either. */
  const insertEmbed = useCallback(() => {
    // eslint-disable-next-line no-alert
    const raw = window.prompt("Video URL (YouTube or Vimeo)");
    const url = (raw || "").trim();
    if (!url) return;
    let src = "";
    try {
      const u = new URL(url);
      if (!EMBED_HOSTS.test(u.hostname)) throw new Error("host");
      if (/youtu\.be$/i.test(u.hostname)) src = `https://www.youtube.com/embed${u.pathname}`;
      else if (/youtube\.com$/i.test(u.hostname)) {
        const v = u.searchParams.get("v");
        src = v ? `https://www.youtube.com/embed/${v}` : `https://www.youtube.com${u.pathname}`;
      } else if (/vimeo\.com$/i.test(u.hostname)) {
        src = `https://player.vimeo.com/video${u.pathname}`;
      }
    } catch (e) {
      src = "";
    }
    if (!src) {
      EventService.emit("showError", {
        message: "Only YouTube and Vimeo links can be embedded in a note.",
      });
      return;
    }
    /* No width/height on the tag: DOMPurify drops both whatever ALLOWED_ATTR
       says (measured, same as it does to <font size>), and a note is read in
       four places at four widths — the stylesheet sizes the frame instead. */
    exec("insertHTML", `<iframe src="${src}"></iframe>`);
  }, [exec]);

  /* Font family and the block type are plain commands; they only need closing. */
  const applyAndClose = useCallback(
    (command, value, css) => {
      exec(command, value, css);
      setMenu(null);
    },
    [exec]
  );

  /* Close whichever toolbar menu is open on an outside click. NOT the block
     menu: it lives outside menuRef, so this listener took a press ON one of its
     items for an outside click and closed the menu on mousedown — the item was
     gone before its click, and H1-H6, Blockquote and Code never applied from a
     mouse (measured; a scripted .click() skips mousedown, which is why the
     toolbar harness never saw it). The block menu has its own listener below. */
  useEffect(() => {
    if (!menu || menu === "block") return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menu]);

  // The block-style dropdown (Normal / Heading 1-3). formatBlock wraps the caret's
  // block, so no text selection is needed.
  const applyBlock = useCallback(
    (tag, label) => {
      exec("formatBlock", `<${tag}>`);
      setBlockLabel(label);
      setMenu(null);
    },
    [exec]
  );

  /* The block dropdown sits in its own container, so it needs its own
     outside-click — on the SHARED state, so opening any other menu closes it
     and two lists can never be on screen at once. */
  useEffect(() => {
    if (menu !== "block") return undefined;
    const onDown = (e) => {
      if (blockRef.current && !blockRef.current.contains(e.target)) setMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menu]);

  /* Capture phase: an open calendar closes itself from a bubbling handler, and
     a bubbling listener here could run after it and see nothing open — closing
     the whole composer, draft and all, on the Escape meant for the calendar. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !popoverOpenRef.current) onClose();
    };
    document.addEventListener("keydown", onKey, true);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  /**
   * Projects for the task panel. Zoho's /v2/tm/* endpoints 404 on dev1 BY DESIGN
   * (see APIConfig.progressNoteTask), so this fetch failing is the NORMAL local
   * case, not an incident: swallow it to an empty list. The modal still opens and
   * a note with no tasks still posts — the Zoho panel must never be able to block
   * note creation.
   */
  useEffect(() => {
    service.getProjects(
      (res) => {
        if (!mounted.current) return;
        // Bridge reads `response.projects` (ProgressNotes.fetchAsanaUsersProjects).
        setProjects((res && (res.projects || res.data)) || []);
      },
      () => {
        if (!mounted.current) return;
        setProjects([]);
      }
    );
  }, [service]);

  const patchTask = (index, patch) =>
    setTasks((prev) => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)));

  /** Users are per-PROJECT, so they hang off the task, not off the modal. */
  const selectProject = (index, project) => {
    patchTask(index, { selectedProject: project, selectedUser: null, users: [] });
    if (!project) return;

    service.getUsersByProject(
      project.id_string,
      (res) => {
        if (!mounted.current) return;
        // Bridge reads `res.users` (fetchRespectiveProjectUsers).
        patchTask(index, { users: (res && (res.users || res.data)) || [] });
      },
      () => {
        if (!mounted.current) return;
        patchTask(index, { users: [] });
        showError("No associated users found.");
      }
    );
  };

  /**
   * Bridge's Save-button guard, per task and in this order: due date, project,
   * description. Messages are bridge's verbatim — ops read them in tickets.
   */
  const validateTasks = () => {
    const errors = {};
    tasks.forEach((t, i) => {
      if (!t.taskDate) errors[i] = "Due date is required for all the tasks.";
      else if (!t.selectedProject) errors[i] = "Project is required for all the tasks.";
      else if (!t.taskText.trim()) errors[i] = "Description is required for all the tasks";
    });
    setTaskErrors(errors);
    return errors;
  };

  /**
   * The task list, spliced into the body as HTML. This is why existing notes
   * render a "Task assigned to / 1. …" block — the tasks live in Zoho, and this
   * markup is the body's only record of them. The `<li>`s are identical in both
   * modes; only the opening header differs (see TASK_HEADER).
   */
  const taskListHtml = () => {
    let out = TASK_HEADER[subject];
    tasks.forEach((t) => {
      const projectLabel = (t.selectedProject && t.selectedProject.name) || "";
      out +=
        "<li>" +
        (t.selectedUser ? "<b>" + t.selectedUser.name + "</b> in " : "") +
        projectLabel +
        " - " +
        t.taskText +
        "</li>  ";
    });
    return out + "</ol>";
  };

  /**
   * One Zoho POST per task (the endpoint takes a single task, not a batch).
   *
   * Each call is wrapped into a promise that RESOLVES either way, so the
   * Promise.all below waits for every task to settle instead of short-circuiting
   * on the first rejection — a fail-fast Promise.all would leave the remaining
   * tasks in flight while we were already deciding not to write the note. Bridge
   * uses Promise.allSettled for this; that is ES2020 and CRA 3 polyfills no
   * built-ins, so we get the same semantics without the compatibility bet.
   */
  const postTasks = () =>
    Promise.all(
      tasks.map((t) => {
        const payload = {
          name: `${patient.firstName} ${patient.lastName} + ${patient.id} + ${
            patient.clientCode || ""
          } + ${t.taskText}`,
          projectId: t.selectedProject.id_string,
          projectName: t.selectedProject.name_string,
          endDate: toZohoDate(t.taskDate),
          personResponsible: t.selectedUser ? String(t.selectedUser.id) : "0",
          personResponsibleName: t.selectedUser ? t.selectedUser.name : "",
          source: "pn",
          patientId: patient.id,
        };
        return new Promise((resolve) =>
          service.newTask(payload, () => resolve(true), () => resolve(false))
        );
      })
    );

  const postNote = (noteBody) => {
    /* The doctor id goes in `employeeId` — a doctor session has no
       employeeProfile at all, in either repo, which is what made every save
       422. See noteAuthor. */
    const note = {
      patientId: patient.id,
      body: noteBody,
      author: noteAuthor(auth.getUserProfile()),
      tag: tagValue(activeTab),
      acknowledgers: [],
      comments: [],
    };

    service.createProgressNote(
      note,
      (res) => {
        if (!mounted.current) return;
        setSaving(false);
        if (onCreated) onCreated(res);
        onClose();
      },
      (err) => {
        if (!mounted.current) return;
        setSaving(false);
        showError(`Could not save the note: ${(err && err.message) || ""}`);
      }
    );
  };

  /**
   * Hand the finished comment body to the parent, which owns the two-call
   * POST+PATCH (a comment carries no note id, so it must be created and then
   * linked) and reports its own failures — including the one where step 1 lands
   * and step 2 does not. So this deliberately does NOT toast on rejection: a
   * second, vaguer message stacked on the parent's precise one would only muddy
   * what actually happened. The modal stays open with the text intact so the
   * clinician can retry without retyping.
   */
  const postComment = async (commentBody) => {
    try {
      await onSubmitComment(commentBody);
    } catch (e) {
      if (mounted.current) setSaving(false);
      return;
    }
    if (!mounted.current) return;
    setSaving(false);
    if (onCreated) onCreated();
    onClose();
  };

  /* WHAT IS MISSING, in the reviewer's words — or "" when nothing is.
     Specific rather than one sentence covering both: "pick a category and type
     a note" is half wrong whenever only one of them is true.

     This is the ONLY rule now: a note is filed UNDER a category, so its tab is
     required; a comment has no tag on the wire, so there is nothing to require
     beyond the text. It used to be stated twice — once here and once as
     `canSave` for the button — and two copies of a rule is one too many. */
  const missing = () => {
    const hasBody = bodyText.trim().length > 0;
    if (!isComment && !activeTab && !hasBody) return `Pick a category and type a ${subject} first`;
    if (!isComment && !activeTab) return "Pick a category first";
    if (!hasBody) return `Type a ${subject} first`;
    return "";
  };

  const handleSave = async () => {
    /* SAVE STAYS LIVE AND SAYS WHY (Nittin, 2026-09-24). A disabled button
       explains nothing: it cannot be clicked, so it cannot answer the question
       clicking it was asking, and its tooltip only appears if you happen to
       hover the thing you have already decided is broken. */
    const why = missing();
    if (why) {
      showError(why);
      return;
    }
    if (saving) return;

    if (tasks.length) {
      const errors = validateTasks();
      const first = Object.keys(errors)[0];
      if (first !== undefined) {
        showError(errors[first]);
        return;
      }
    }

    setSaving(true);

    // SANITIZE the editor's HTML, THEN append our own task HTML. sanitizeBody
    // (DOMPurify, ALLOWED_TAGS) is what makes rich text safe to write: the
    // editor only produces b/i/u/s/lists/links, and anything else — a pasted
    // <script>, a `javascript:` href — is stripped here. phr sanitizes on read,
    // but BRIDGE renders these bodies raw, so we must never author markup bridge
    // would execute. This is the ONLY place the body is finalised.
    normalizeTyped(); // a <font> typed in the last instant, e.g. by an IME
    const rawHtml = editorRef.current ? editorRef.current.innerHTML : body;
    let wireBody = sanitizeBody(rawHtml);

    if (tasks.length) {
      wireBody += taskListHtml();

      /**
       * ORDER IS LOAD-BEARING: create the Zoho tasks FIRST and only write the
       * body if every one of them landed. That body claims those tasks exist; one
       * advertising work that was never queued is worse than none at all, and it
       * cannot be retracted once the feed has it. Bridge does the same in both
       * paths (handleSubmit -> await newTask() -> addProgressNote/addComment).
       */
      const results = await postTasks();
      if (!mounted.current) return;

      if (results.some((ok) => !ok)) {
        setSaving(false);
        showError("Error while saving tasks please try again after submitting.");
        return;
      }
    }

    if (isComment) postComment(wireBody);
    else postNote(wireBody);
  };

  const title = isComment ? "New comment" : "New progress note";

  const req = (
    <span className="pn-req" aria-hidden="true">
      *
    </span>
  );

  return ReactDOM.createPortal(
    <div
      className="pn-modal-overlay"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="pn-modal" role="dialog" aria-modal="true" aria-label={title}>
          <div className="pn-modal-head">
            <div className="pn-modal-head-left">
              <button type="button" className="pn-modal-close" aria-label="Close" onClick={onClose}>
                <CloseIcon />
              </button>
              <span className="pn-modal-title">{title}</span>
            </div>
            <button
              type="button"
              className="pn-save"
              onClick={handleSave}
              /* Disabled ONLY while the write is in flight — that is the one
                 state where a second click would do harm. An incomplete note
                 leaves the button live and answers on click. */
              disabled={saving}
              /* No greyed-out look for an incomplete note: it is clickable and
                 it answers, so dressing it as dead would be the same lie the
                 disabled state told. Only the in-flight state dims it. */
              style={saving ? DISABLED_STYLE : undefined}
              title={saving ? "Saving…" : "Save"}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
  
          <div className="pn-modal-body">
            {/* LEFT — the editor, over the tab row (note) or the quote (comment) */}
            <div className="pn-pane-left">
              {isComment ? (
                <div className="pn-quote">
                  <span className="pn-quote-caption">Replying to</span>
                  {/*
                    `replyingTo.html` is the parent CARD's html — toCard already ran
                    it through sanitizeBody, so it is the same string the feed
                    itself injects. It must NEVER be a raw `note.body` off the wire:
                    that is unescaped author input straight from the database, and
                    injecting it here would open in the composer exactly the hole
                    the feed goes out of its way to close.
                  */}
                  <div
                    className="pn-quote-body"
                    dangerouslySetInnerHTML={{ __html: replyingTo.html }}
                  />
                </div>
              ) : (
                <div className="pn-tabs">
                  {TAB_CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      className={`pn-chip${cat === activeTab ? " is-active" : ""}`}
                      aria-pressed={cat === activeTab}
                      onClick={() => setActiveTab(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
  
              <div className="pn-editor">
                {/* THE ROW IS LEGACY'S, IN ITS ORDER: inline, blockType,
                    fontSize, fontFamily, list, textAlign, colorPicker, link,
                    embedded, emoji, image, remove, history — react-draft-
                    wysiwyg's defaultToolbar, which is what legacy renders. */}
                <div className="pn-toolbar">
                  {/* inline */}
                  <button type="button" className="pn-tool-btn pn-tool-b" title="Bold" aria-label="Bold"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")}>
                    B
                  </button>
                  <button type="button" className="pn-tool-btn pn-tool-i" title="Italic" aria-label="Italic"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")}>
                    I
                  </button>
                  <button type="button" className="pn-tool-btn pn-tool-u" title="Underline" aria-label="Underline"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("underline")}>
                    U
                  </button>
                  <button type="button" className="pn-tool-btn pn-tool-s" title="Strikethrough" aria-label="Strikethrough"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("strikeThrough")}>
                    S
                  </button>
                  <button type="button" className="pn-tool-btn pn-tool-code" title="Monospace" aria-label="Monospace"
                    onMouseDown={(e) => e.preventDefault()} onClick={codeSpan}>
                    {"{}"}
                  </button>
                  <button type="button" className="pn-tool-btn pn-tool-sup" title="Superscript" aria-label="Superscript"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("superscript")}>
                    x<sup>2</sup>
                  </button>
                  <button type="button" className="pn-tool-btn pn-tool-sub" title="Subscript" aria-label="Subscript"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("subscript")}>
                    x<sub>2</sub>
                  </button>

                  <span className="pn-tool-sep" aria-hidden="true" />

                  {/* blockType, fontSize, fontFamily — three dropdowns, one
                      open-menu state, one outside-click effect. */}
                  <div className="pn-tool-block" ref={blockRef}>
                    <button
                      type="button"
                      className="pn-tool-pill"
                      title="Block type"
                      aria-haspopup="menu"
                      aria-expanded={menu === "block"}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setMenu((m) => (m === "block" ? null : "block"))}
                    >
                      {blockLabel} <Chevron />
                    </button>
                    {menu === "block" && (
                      <ul className="pn-block-menu" role="menu">
                        {BLOCKS.map((bl) => (
                          <li key={bl.tag}>
                            <button
                              type="button"
                              role="menuitem"
                              className={`pn-block-item pn-block-${bl.tag}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => applyBlock(bl.tag, bl.label)}
                            >
                              {bl.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="pn-tool-block" ref={menuRef}>
                    <button type="button" className="pn-tool-pill pn-tool-size" title="Font size" aria-haspopup="menu"
                      aria-expanded={menu === "size"}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setMenu((m) => (m === "size" ? null : "size"))}>
                      {sizeLabel} <Chevron />
                    </button>
                    <button type="button" className="pn-tool-pill" title="Font" aria-haspopup="menu"
                      aria-expanded={menu === "font"}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setMenu((m) => (m === "font" ? null : "font"))}>
                      {fontLabel} <Chevron />
                    </button>

                    {menu === "size" && (
                      <ul className="pn-block-menu pn-size-menu" role="menu">
                        {SIZES.map((sz) => (
                          <li key={sz}>
                            <button type="button" role="menuitem" className="pn-block-item"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => { setSizeLabel(String(sz)); applySize(sz); }}>
                              {sz}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {menu === "font" && (
                      <ul className="pn-block-menu pn-font-menu" role="menu">
                        {FONTS.map((f) => (
                          <li key={f}>
                            <button type="button" role="menuitem" className="pn-block-item"
                              style={{ fontFamily: f }}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => { setFontLabel(f); applyFont(f); }}>
                              {f}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {menu === "color" && (
                      <div className="pn-block-menu pn-color-menu" role="menu">
                        {/* Legacy's picker has these two tabs, in this order. */}
                        <div className="pn-color-tabs">
                          <button type="button"
                            className={`pn-color-tab${colorMode === "color" ? " is-on" : ""}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setColorMode("color")}>
                            Text
                          </button>
                          <button type="button"
                            className={`pn-color-tab${colorMode === "bg" ? " is-on" : ""}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setColorMode("bg")}>
                            Highlight
                          </button>
                        </div>
                        <div className="pn-color-grid">
                          {COLORS.map((c) => (
                            <button key={c} type="button" role="menuitem" className="pn-color-swatch"
                              style={{ background: c }} title={c} aria-label={c}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => applyColor(c)} />
                          ))}
                        </div>
                      </div>
                    )}
                    {menu === "emoji" && (
                      <ul className="pn-block-menu pn-emoji-menu" role="menu">
                        {EMOJIS.map((em) => (
                          <li key={em}>
                            <button type="button" role="menuitem" className="pn-emoji-item"
                              onMouseDown={(ev) => ev.preventDefault()}
                              onClick={() => applyAndClose("insertText", em)}>
                              {em}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <span className="pn-tool-sep" aria-hidden="true" />

                  {/* list: unordered, ordered, indent, outdent — legacy's order */}
                  <button type="button" className="pn-tool-btn" title="Bulleted list" aria-label="Bulleted list"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")}>
                    <BulletIcon />
                  </button>
                  <button type="button" className="pn-tool-btn" title="Numbered list" aria-label="Numbered list"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertOrderedList")}>
                    <NumberListIcon />
                  </button>
                  <button type="button" className="pn-tool-btn" title="Increase indent" aria-label="Increase indent"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("indent")}>
                    <IndentIcon />
                  </button>
                  <button type="button" className="pn-tool-btn" title="Decrease indent" aria-label="Decrease indent"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("outdent")}>
                    <IndentIcon out />
                  </button>

                  <span className="pn-tool-sep" aria-hidden="true" />

                  {/* textAlign */}
                  <button type="button" className="pn-tool-btn" title="Align left" aria-label="Align left"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("justifyLeft")}>
                    <AlignIcon lines={[[1, 14], [1, 9], [1, 14], [1, 9]]} />
                  </button>
                  <button type="button" className="pn-tool-btn" title="Align centre" aria-label="Align centre"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("justifyCenter")}>
                    <AlignIcon lines={[[1, 14], [3.5, 9], [1, 14], [3.5, 9]]} />
                  </button>
                  <button type="button" className="pn-tool-btn" title="Align right" aria-label="Align right"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("justifyRight")}>
                    <AlignIcon lines={[[1, 14], [6, 9], [1, 14], [6, 9]]} />
                  </button>
                  <button type="button" className="pn-tool-btn" title="Justify" aria-label="Justify"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("justifyFull")}>
                    <AlignIcon lines={[[1, 14], [1, 14], [1, 14], [1, 14]]} />
                  </button>

                  <span className="pn-tool-sep" aria-hidden="true" />

                  {/* colorPicker */}
                  <button type="button" className="pn-tool-btn pn-tool-color" title="Text colour"
                    aria-label="Text colour" aria-haspopup="menu" aria-expanded={menu === "color"}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setMenu((m) => (m === "color" ? null : "color"))}>
                    A<span className="pn-color-bar" aria-hidden="true" />
                  </button>

                  {/* link: link, unlink */}
                  <button type="button" className="pn-tool-btn" title="Insert link" aria-label="Insert link"
                    onMouseDown={(e) => e.preventDefault()} onClick={insertLink}>
                    <LinkIcon />
                  </button>
                  <button type="button" className="pn-tool-btn" title="Remove link" aria-label="Remove link"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("unlink")}>
                    <UnlinkIcon />
                  </button>

                  {/* embedded */}
                  <button type="button" className="pn-tool-btn" title="Embed video" aria-label="Embed video"
                    onMouseDown={(e) => e.preventDefault()} onClick={insertEmbed}>
                    <EmbedIcon />
                  </button>

                  {/* emoji */}
                  <button type="button" className="pn-tool-btn" title="Emoji" aria-label="Emoji"
                    aria-haspopup="menu" aria-expanded={menu === "emoji"}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setMenu((m) => (m === "emoji" ? null : "emoji"))}>
                    <span aria-hidden="true">🙂</span>
                  </button>

                  {/* image */}
                  <button type="button" className="pn-tool-btn" title="Insert image" aria-label="Insert image"
                    onMouseDown={(e) => e.preventDefault()} onClick={insertImage}>
                    <ImageIcon />
                  </button>

                  {/* remove */}
                  <button type="button" className="pn-tool-btn" title="Clear formatting" aria-label="Clear formatting"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("removeFormat")}>
                    <ClearIcon />
                  </button>

                  {/* history: undo, redo */}
                  <button type="button" className="pn-tool-btn" title="Undo" aria-label="Undo"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("undo")}>
                    <UndoIcon />
                  </button>
                  <button type="button" className="pn-tool-btn" title="Redo" aria-label="Redo"
                    onMouseDown={(e) => e.preventDefault()} onClick={() => exec("redo")}>
                    <UndoIcon redo />
                  </button>
                </div>
  
                <div
                  ref={editorRef}
                  className="pn-editor-area"
                  contentEditable
                  suppressContentEditableWarning
                  role="textbox"
                  aria-multiline="true"
                  aria-label={isComment ? "Comment body" : "Note body"}
                  data-placeholder="Type here …"
                  onInput={onEditorInput}
                  onCompositionEnd={onEditorInput}
                />
              </div>
            </div>
  
            {/* RIGHT — tasks */}
            <div className="pn-pane-right">
              <div className="pn-tasks-head">
                <h3 className="pn-tasks-title">Tasks ({tasks.length})</h3>
                <button
                  type="button"
                  className="pn-add-task"
                  onClick={() =>
                    setTasks((prev) =>
                      prev.length < MAX_TASKS ? [...prev, emptyTask()] : prev
                    )
                  }
                  disabled={tasks.length >= MAX_TASKS}
                  style={tasks.length >= MAX_TASKS ? DISABLED_STYLE : undefined}
                  title={
                    tasks.length >= MAX_TASKS
                      ? `A ${subject} can carry at most ${MAX_TASKS} tasks`
                      : "Add task"
                  }
                >
                  <span className="pn-add-plus" aria-hidden="true">
                    +
                  </span>{" "}
                  Add task
                </button>
              </div>
  
              {tasks.map((task, index) => (
                <div className="pn-task" key={task.key}>
                  <div className="pn-field">
                    <div className="pn-field-row">
                      <span className="pn-field-label">Description {req}</span>
                      <button
                        type="button"
                        className="pn-reset"
                        onClick={() =>
                          setTasks((prev) =>
                            prev.map((t, i) =>
                              i === index ? { ...emptyTask(), key: t.key } : t
                            )
                          )
                        }
                        title="Clear this task"
                      >
                        Reset
                      </button>
                    </div>
                    <textarea
                      className="pn-textarea"
                      value={task.taskText}
                      onChange={(e) => patchTask(index, { taskText: e.target.value })}
                      aria-label="Task description"
                    />
                  </div>
  
                  <div className="pn-field">
                    <span className="pn-field-label">Project {req}</span>
                    <span className="pn-select-wrap">
                      <select
                        className="pn-select"
                        value={task.selectedProject ? task.selectedProject.id_string : ""}
                        onChange={(e) =>
                          selectProject(
                            index,
                            projects.find((p) => p.id_string === e.target.value) || null
                          )
                        }
                        aria-label="Project"
                        title={
                          projects.length
                            ? "Project"
                            : "No projects available — Zoho is prod-only (see APIConfig.progressNoteTask)"
                        }
                      >
                        <option value="">
                          {projects.length ? "Select a project" : "No projects available"}
                        </option>
                        {projects.map((p) => (
                          <option key={p.id_string} value={p.id_string}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      <Chevron />
                    </span>
                  </div>
  
                  <div className="pn-task-row">
                    <div className="pn-field pn-field-half">
                      <span className="pn-field-label">Assign to</span>
                      <span className="pn-select-wrap">
                        <select
                          className="pn-select"
                          value={task.selectedUser ? String(task.selectedUser.id) : ""}
                          disabled={!task.selectedProject}
                          onChange={(e) =>
                            patchTask(index, {
                              // "" is the None option -> personResponsible "0".
                              selectedUser:
                                task.users.find((u) => String(u.id) === e.target.value) ||
                                null,
                            })
                          }
                          aria-label="Assign to"
                          title={task.selectedProject ? "Assign to" : "Pick a project first"}
                        >
                          <option value="">None</option>
                          {task.users.map((u) => (
                            <option key={u.id} value={String(u.id)}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                        <Chevron />
                      </span>
                    </div>
  
                    <div className="pn-field pn-field-half">
                      <span className="pn-field-label">Due Date {req}</span>
                      <span className="pn-date-wrap">
                        {/* Our calendar, not <input type="date"> (2026-10-05):
                            Chrome's picker moved its highlight to the same day
                            in every month browsed, so "5 Nov" looked chosen.
                            Open-ended, as the native input was; the value is
                            still a local "YYYY-MM-DD" for toZohoDate. */}
                        <DatePicker
                          id={`pn-task-date-${task.key}`}
                          value={task.taskDate}
                          minDays={null}
                          maxDays={null}
                          menuClassName="pn-date-menu"
                          onOpenChange={onCalendarOpenChange}
                          onChange={(key) => patchTask(index, { taskDate: key })}
                        />
                      </span>
                    </div>
                  </div>
  
                  {/* Figma draws an "Escalate to Slack" checkbox here, but it is
                      dead in BOTH apps: the live Zoho task payload has no Slack
                      field (only bridge's removed Asana block did), and the team
                      confirmed there is no backend for it. Shipping a control that
                      silently does nothing is worse than omitting it, so the row is
                      just the remove-task button. */}
                  <div className="pn-task-actions">
                    <button
                      type="button"
                      className="pn-remove"
                      aria-label="Remove task"
                      title="Remove task"
                      onClick={() => setTasks((prev) => prev.filter((_t, i) => i !== index))}
                    >
                      <MinusIcon />
                    </button>
                  </div>
  
                  {taskErrors[index] ? (
                    <span style={ERROR_STYLE} role="alert">
                      {taskErrors[index]}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>,
    document.body
  );
};

ComposerModal.propTypes = {
  /** The patient record — id/firstName/lastName/clientCode go into both payloads. */
  patient: PropTypes.shape({
    id: PropTypes.number.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    clientCode: PropTypes.string,
  }).isRequired,
  /**
   * The note being replied to — presence is what puts the modal in COMMENT mode.
   * A toCard() card: `html` is ALREADY sanitizeBody's output (never a raw wire
   * body — see the quote block). Absent → the "New progress note" screen.
   */
  replyingTo: PropTypes.shape({
    id: PropTypes.string,
    html: PropTypes.string,
  }),
  /**
   * The category tab to open on — the feed's active filter chip. Ignored in
   * comment mode and for the "ALL" pseudo-chip, so the caller can pass whatever
   * it is filtered by without testing it first.
   */
  initialCategory: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  /**
   * Note mode: called with the created note so the feed can refresh/prepend.
   * Comment mode: called with nothing once the comment is on the record.
   */
  onCreated: PropTypes.func,
  /**
   * (body) -> Promise, where `body` is the finished wire body — escaped and
   * task-spliced HERE, so the parent must not touch it again. The parent owns the
   * comment's two-call POST+PATCH.
   *
   * Validated conditionally rather than declared required: it is meaningless in
   * note mode, but forgetting it in comment mode would surface only as a Save
   * that throws at the click.
   */
  onSubmitComment: (props, name, component) =>
    props.replyingTo && typeof props[name] !== "function"
      ? new Error(
          `The prop \`${name}\` is marked as required in \`${component}\` when ` +
            "`replyingTo` is set, but its value is `" + typeof props[name] + "`."
        )
      : null,
};

export default ComposerModal;
